/**
 * The calculation of a Vorgang: what the pricing engine made of the parts
 * list, with every amount, its basis and its source (design 2026-09-13 §8,
 * §9). Transport for `/api/v1/cases/{id}/calculations/*` and
 * `/api/v1/pricing/source`.
 *
 * The wire types below are aliases on `components["schemas"][…]` from
 * `@k60/contracts` — the generated client, not a hand-written copy. Names
 * and casing are the contract's — `WireModel` writes camelCase.
 *
 * Amounts and percentages travel as decimal **strings** (D-17) and are
 * parsed only in `@/lib/format`, only for display. Nothing in this file or
 * in any screen adds, rounds or compares a price.
 */

import { useQuery } from "@tanstack/react-query";

import { ProblemError, toProblemDetails, type components } from "@k60/contracts";

import { StaleVersionError } from "./cases";
import { api } from "./client";

type S = components["schemas"];

export type CalculationOut = S["CalculationOut"];
export type CalculationLineOut = S["CalculationLineOut"];
export type CalculationParamsOut = S["CalculationParamsOut"];
export type OptionsOut = S["OptionsOut"];
export type OptionOut = S["OptionOut"];
export type PriceSourceRefOut = S["PriceSourceRefOut"];
export type PriceBasisOut = S["PriceBasisOut"];
export type RuleTrailStepOut = S["RuleTrailStepOut"];
export type BlockedReasonOut = S["BlockedReasonOut"];
export type SupplierAlternativeOut = S["SupplierAlternativeOut"];
export type BlockerOut = S["BlockerOut"];
export type GapOut = S["GapOut"];
export type HintOut = S["HintOut"];
export type PriceStateOut = S["PriceStateOut"];
export type PriceStateSeenOut = S["PriceStateSeenOut"];
export type ReviewOut = S["ReviewOut"];
export type CalculationIn = S["CalculationIn"];
export type ApproveIn = S["ApproveIn"];
export type WithdrawIn = S["WithdrawIn"];
export type ConflictOut = S["ConflictOut"];
export type RowCountsOut = S["RowCountsOut"];
export type PriceSourceStatusOut = S["PriceSourceStatusOut"];
export type LineKind = CalculationLineOut["kind"];
export type PriceRule = CalculationLineOut["priceRule"];

/**
 * The sentence `CalculationRoute` matches on to tell „no calculation yet“
 * apart from „the server could not be reached“ — a constant, like
 * `NO_PLAN_YET`, because a comparison against a copy is a comparison that
 * drifts.
 */
export const NO_CALC_YET = "Dieser Vorgang hat noch keine Kalkulation.";

const NEVER_SYNCED = "Der Preisspiegel wurde noch nie befüllt.";

/**
 * Thrown by `runCalculation` on a 503: no successful `price-sync` has ever
 * run, so there is nothing to price against (design §4.2). Its own class
 * because the screen answers it with a banner of its own, not with the
 * generic „nicht gespeichert“ line — and because it is our operations
 * failure, not the person's.
 */
export class PriceSourceUnreachableError extends Error {
  readonly name = "PriceSourceUnreachableError";

  constructor(detail: string | null) {
    super(detail ?? NEVER_SYNCED);
  }
}

/**
 * Under `["case", caseId]` — not because a planning gesture invalidates it.
 * `PlanningScreen.persist` never does on an accepted version; only its
 * `reload()` does, and only after a stale or refused write, which is a
 * conflict path, not the normal one. This query carries no `staleTime`
 * override, so the calculation screen reads `stale` fresh from the server on
 * every mount instead of trusting a cached answer to still describe the
 * current Stand (D-11, D-22).
 */
export const CALC_KEY = (caseId: string | null) =>
  ["case", caseId, "calculation", "latest"] as const;

export const PRICE_SOURCE_KEY = ["pricing", "source"] as const;

/**
 * What the first calculation of a Vorgang is run with (D-5, KF-17) —
 * `params.default_params` on the server, spelled out here because
 * `CalculationIn` requires every field and the first POST has no earlier
 * calculation to copy them from. Barfuß gets its own mesh (KF-14).
 */
export function defaultParams(gratingType: string | null): Omit<CalculationIn, "expectedVersionNo"> {
  return {
    material: "verzinkt",
    tragstab: "30x2",
    maschenweite: gratingType === "barfuss" ? "25/11" : "30/30",
    rutschhemmung: "keine",
    marginPct: "30",
    supplierOverride: null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * One refusal, whichever shape it arrived in. A 409 that names the current
 * Stand is the optimistic lock speaking (`StaleVersionError`, as on
 * `postVersion`); a 409 without one is a sentence to show („freigegeben —
 * zuerst zurückziehen“); a 503 is the mirror missing; everything else is the
 * client's own `ProblemError`, or one built the way the middleware builds it.
 */
function refused(status: number, payload: unknown, thrown: ProblemError | null): Error {
  const body = isRecord(payload) ? payload : {};
  if (status === 409 && typeof body.latestVersionNo === "number") {
    return new StaleVersionError(body.latestVersionNo);
  }
  if (status === 503) {
    const detail = typeof body.detail === "string" ? body.detail : (thrown?.problem.detail ?? null);
    return new PriceSourceUnreachableError(detail);
  }
  return thrown ?? new ProblemError(toProblemDetails(payload, status), null);
}

/**
 * `api.GET`/`api.POST` directly (Task 9): `paths` in `@k60/contracts` now
 * knows all five calculation routes plus `/api/v1/pricing/source`, so the
 * type-cast shim this file used to carry (`Reply`/`Init`/`transport`) is
 * gone. The correlation header and the error middleware still run inside
 * `api` itself (`./client`); this wrapper only turns a non-2xx or
 * empty-body reply into the right refusal.
 */
async function post<T>(
  request: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<{ data: T; status: number }> {
  let reply: { data?: T; error?: unknown; response: Response };
  try {
    reply = await request();
  } catch (cause) {
    if (cause instanceof ProblemError) throw refused(cause.status, cause.problem.raw, cause);
    throw cause;
  }
  if (!reply.response.ok || reply.data === undefined) {
    throw refused(reply.response.status, reply.error, null);
  }
  return { data: reply.data, status: reply.response.status };
}

/**
 * `GET /api/v1/cases/{id}/calculations/latest`. A 404 is the ordinary
 * answer for a Vorgang nobody has priced yet — it becomes `NO_CALC_YET` and
 * is not retried; every other failure keeps the app's one retry.
 */
export function useLatestCalculation(caseId: string | null) {
  return useQuery({
    queryKey: CALC_KEY(caseId),
    enabled: caseId !== null,
    retry: (count, error) => error.message !== NO_CALC_YET && count < 1,
    queryFn: async (): Promise<CalculationOut> => {
      try {
        const { data, response } = await api.GET("/api/v1/cases/{case_id}/calculations/latest", {
          params: { path: { case_id: caseId! } },
        });
        if (response.status === 404) throw new Error(NO_CALC_YET);
        if (!response.ok || !data) throw new Error("Kalkulation konnte nicht geladen werden.");
        return data;
      } catch (cause) {
        if (cause instanceof ProblemError && cause.status === 404) throw new Error(NO_CALC_YET);
        throw cause;
      }
    },
  });
}

/**
 * `POST /api/v1/cases/{id}/calculations`. `created` tells 201 from 200:
 * identical parameters on an identical price state give the stored row back
 * (D-16), and the screen says „unverändert“ instead of pretending a run.
 */
export async function runCalculation(
  caseId: string,
  input: CalculationIn,
): Promise<{ calc: CalculationOut; created: boolean }> {
  const { data, status } = await post(() =>
    api.POST("/api/v1/cases/{case_id}/calculations", {
      params: { path: { case_id: caseId } },
      body: input,
    }),
  );
  return { calc: data, created: status === 201 };
}

/** `POST …/calculations/{calcId}/approve` — the human signature (A-014, E-52). */
export async function approveCalculation(
  caseId: string,
  calcId: string,
  input: ApproveIn,
): Promise<CalculationOut> {
  const { data } = await post(() =>
    api.POST("/api/v1/cases/{case_id}/calculations/{calc_id}/approve", {
      params: { path: { case_id: caseId, calc_id: calcId } },
      body: input,
    }),
  );
  return data;
}

/** `POST …/calculations/{calcId}/withdraw` — takes the signature back, with a reason. */
export async function withdrawApproval(
  caseId: string,
  calcId: string,
  input: WithdrawIn,
): Promise<CalculationOut> {
  const { data } = await post(() =>
    api.POST("/api/v1/cases/{case_id}/calculations/{calc_id}/withdraw", {
      params: { path: { case_id: caseId, calc_id: calcId } },
      body: input,
    }),
  );
  return data;
}

/**
 * The two files of a calculation (N-20, Entscheid Denis 23.09.2026):
 * `angebot` may leave the house — no purchase price, no margin —, `intern`
 * carries exactly those and stays inside.
 */
export type WorkbookVariant = "angebot" | "intern";

const WORKBOOK_FALLBACK_NAME: Record<WorkbookVariant, string> = {
  angebot: "Angebot.xlsx",
  intern: "Kalkulation-intern.xlsx",
};

/** The quote before its release, where the server's name went missing (review
 * Task 11, Nr. 6): `Entwurf_` in front, as the server names it. */
const DRAFT_FALLBACK_NAME = "Entwurf_Angebot.xlsx";

/**
 * The file name the server chose (RFC 6266): `filename*=UTF-8''…` first —
 * it keeps the supplier's umlauts —, then the ASCII `filename="…"`, then a
 * generic name rather than none.
 */
function workbookName(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (extended) {
    try {
      return decodeURIComponent(extended[1]!.trim());
    } catch {
      // A malformed escape falls through to the plain name below.
    }
  }
  const plain = /filename="([^"]+)"/i.exec(disposition);
  return plain ? plain[1]! : fallback;
}

/**
 * `GET …/calculations/{calcId}/export/{angebot|intern}.xlsx` — one of the
 * two workbooks. Two fixed paths rather than a free parameter, so the typed
 * client knows both. Through the one client, so the correlation id and the
 * error middleware apply; `parseAs: "blob"` because the body is a file, and
 * `credentials: "same-origin"` spelled out because the route answers 401
 * without the session cookie.
 *
 * `draft` (the quote before its release, E-52) only names the fallback: the
 * server decides what the file says and what it is called — a header a proxy
 * dropped must not turn a draft into a file named like an offer.
 */
export async function downloadCalculationWorkbook(
  caseId: string,
  calcId: string,
  variant: WorkbookVariant,
  { draft = false }: { draft?: boolean } = {},
): Promise<{ blob: Blob; filename: string }> {
  const options = {
    params: { path: { case_id: caseId, calc_id: calcId } },
    parseAs: "blob",
    credentials: "same-origin",
  } as const;
  const { data, response } =
    variant === "angebot"
      ? await api.GET("/api/v1/cases/{case_id}/calculations/{calc_id}/export/angebot.xlsx", options)
      : await api.GET("/api/v1/cases/{case_id}/calculations/{calc_id}/export/intern.xlsx", options);
  if (!response.ok || !data) {
    throw new ProblemError(toProblemDetails(null, response.status), null);
  }
  const fallback = variant === "angebot" && draft ? DRAFT_FALLBACK_NAME : WORKBOOK_FALLBACK_NAME[variant];
  return {
    blob: data,
    filename: workbookName(response.headers.get("Content-Disposition"), fallback),
  };
}

/** `GET /api/v1/pricing/source` — the last mirror run, for the empty state. */
export async function getPricingSource(): Promise<PriceSourceStatusOut> {
  const { data, response } = await api.GET("/api/v1/pricing/source");
  if (!response.ok || !data) {
    throw new ProblemError(toProblemDetails(null, response.status), null);
  }
  return data;
}

export function usePricingSource() {
  return useQuery({
    queryKey: PRICE_SOURCE_KEY,
    queryFn: getPricingSource,
    staleTime: 60_000,
    retry: false,
  });
}
